import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { REVIEW_MEDIAN_OPTIONS, type CourseDetail, type SessionResponse } from "../../shared/types";
import { LoginWallCard } from "../components/LoginWallCard";
import { StaticDataState } from "../components/StaticDataState";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { createReview } from "../lib/api";
import {
  readPendingReviewState,
  savePendingReviewState,
  type PendingReviewState,
} from "../lib/pending-submissions";
import { markUserHasPostedReview } from "../lib/review-encouragement";
import { LOGIN_WALL_ENABLED } from "../lib/site-config";
import { fetchCourse, StaticDataNotFoundError } from "../lib/static-data";
import { formatOffering } from "../lib/offerings";
import { sortLegacyReviewsByMostRecent } from "../../shared/review-snapshot";
import { compareTermsByMostRecent } from "../../shared/terms";

export function CoursePage(props: {
  session: SessionResponse["user"];
}) {
  const { slug = "" } = useParams();
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [isMissing, setIsMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>();
  const [reviewDraft, setReviewDraft] = useState({
    professor: "",
    quarter: "",
    median: "Unsure" as typeof REVIEW_MEDIAN_OPTIONS[number],
    comments: "",
  });
  const [reviewProfessorFilter, setReviewProfessorFilter] = useState("");
  const [reviewMessage, setReviewMessage] = useState<string | null>(null);
  const [pendingReviewState, setPendingReviewState] = useState<PendingReviewState | null>(null);
  const loginWallActive = LOGIN_WALL_ENABLED && !props.session;
  const userId = props.session?.id;
  const reviewCharacterCount = reviewDraft.comments.trim().length;

  useEffect(() => {
    if (!slug) {
      return;
    }

    setCourse(null);
    setIsMissing(false);
    setLoadError(null);
    void fetchCourse(slug)
      .then((nextCourse) => {
        setCourse(nextCourse);
        setIsMissing(false);
      })
      .catch((error) => {
        setCourse(null);
        if (error instanceof StaticDataNotFoundError) {
          setIsMissing(true);
          return;
        }
        setLoadError("Could not load this course page right now.");
      });
  }, [reloadKey, slug]);

  useEffect(() => {
    if (!course) {
      return;
    }
    if (loginWallActive) {
      setPendingReviewState(null);
      setReviewProfessorFilter("");
      return;
    }
    setPendingReviewState(readPendingReviewState(userId, course.courseId, course.generatedAt));
    setReviewProfessorFilter("");
  }, [course, loginWallActive, userId]);

  async function handleSubmitReview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!course) {
      return;
    }
    if (pendingReviewState) {
      setReviewMessage("Your review is already queued for the next nightly update.");
      return;
    }

    const normalizedProfessor = reviewDraft.professor.trim();
    const normalizedQuarter = reviewDraft.quarter.trim().toUpperCase();
    const normalizedComments = reviewDraft.comments.trim();

    if (!normalizedProfessor) {
      setReviewMessage("Choose a professor for this review.");
      return;
    }
    if (!normalizedQuarter) {
      setReviewMessage("Choose the Dartmouth quarter when you took this course.");
      return;
    }
    if (normalizedComments.length < 50) {
      setReviewMessage("Reviews must be at least 50 characters.");
      return;
    }

    try {
      const response = await createReview(course.courseId, {
        ...reviewDraft,
        professor: normalizedProfessor,
        quarter: normalizedQuarter,
        comments: normalizedComments,
        turnstileToken,
      });
      const nextPendingReview = savePendingReviewState(userId, course.courseId, {
        publishAt: response.publishAt,
        professor: normalizedProfessor,
        quarter: normalizedQuarter,
        median: reviewDraft.median,
        comments: normalizedComments,
      });
      markUserHasPostedReview(userId);
      setPendingReviewState(nextPendingReview);
      setReviewMessage("Your review is queued and will appear after the next nightly update, usually within 24 hours.");
      setReviewDraft({ professor: "", quarter: "", median: "Unsure", comments: "" });
    } catch (error) {
      setReviewMessage(error instanceof Error ? error.message : "Could not save review.");
    }
  }

  if (loadError) {
    return <StaticDataState title="Course page could not load." message={loadError} error onRetry={() => setReloadKey((value) => value + 1)} />;
  }

  if (!course && !isMissing) {
    return <section className="panel">Loading course details...</section>;
  }

  if (!course) {
    return (
      <section className="panel stack-md">
        <div className="section-heading">
          <p className="eyebrow">Course</p>
          <h1>That course is not in the catalog.</h1>
        </div>
      </section>
    );
  }

  const reviews = sortLegacyReviewsByMostRecent(course.legacyReviews);
  const reviewCount = reviews.length;
  const reviewProfessorOptions = [...new Set(reviews.map((review) => review.professor).filter(Boolean))].sort();
  const professorSlugByName = new Map(
    course.professors
      .filter((professor) => professor.slug)
      .map((professor) => [professor.name.trim().replace(/\s+/g, " ").toLowerCase(), professor.slug as string] as const),
  );
  const allowedReviewProfessors = [...new Set([
    ...course.professorNames,
    ...course.professors.map((professor) => professor.name),
    ...course.officialOfferings.flatMap((offering) => offering.instructors),
  ].map((name) => name.trim()).filter(Boolean))].sort();
  const allowedReviewTerms = [...new Set(
    course.officialOfferings.map((offering) => offering.term.trim().toUpperCase()).filter(Boolean),
  )].sort(compareTermsByMostRecent);
  const visibleReviews = reviewProfessorFilter
    ? reviews.filter((review) => review.professor === reviewProfessorFilter)
    : reviews;
  const recentOfferings = [...course.officialOfferings]
    .sort((left, right) => compareTermsByMostRecent(left.term, right.term))
    .slice(0, 3);
  const mostRecentOfferingLabel = recentOfferings[0]
    ? `Offered ${formatOffering(recentOfferings[0])}`
    : course.offeredLabel;
  const pendingReviewPreview = pendingReviewState;
  const pendingReviewMessage = pendingReviewState
    ? `Your review for ${pendingReviewState.professor} (${pendingReviewState.quarter}) is pending and should appear after the next nightly update, usually within 24 hours.`
    : null;
  const showPendingReviewPreview = Boolean(
    pendingReviewState
      && (!reviewProfessorFilter || pendingReviewState.professor === reviewProfessorFilter),
  );
  const canSubmitReview = allowedReviewProfessors.length > 0 && allowedReviewTerms.length > 0;
  const typicalMedianLabel = course.typicalMedian ?? null;
  const typicalMedianReviewCount = course.typicalMedianReviewCount ?? 0;
  const reviewPreviewWallActive = !props.session;
  const visibleReviewPreview = reviewPreviewWallActive ? visibleReviews.slice(0, 3) : visibleReviews;
  const hiddenReviewPreview = reviewPreviewWallActive ? visibleReviews.slice(3) : [];

  function renderReviewProfessor(professorName: string) {
    const normalizedProfessorName = professorName.trim().replace(/\s+/g, " ").toLowerCase();
    const professorSlug = professorSlugByName.get(normalizedProfessorName);
    if (!professorSlug) {
      return <span>with {professorName}</span>;
    }

    return (
      <span>
        with{" "}
        <Link to={`/professor/${professorSlug}/`} className="review-professor-link">
          {professorName}
        </Link>
      </span>
    );
  }

  return (
    <div className="stack-xl">
      <section className="course-hero">
        <div className="course-copy">
          <p className="eyebrow">{course.departmentName || course.department}</p>
          <h1>
            {course.shortName}: {course.title}
          </h1>
          {course.description ? <p className="lede course-description">{course.description}</p> : null}
          <div className="course-meta">
            {mostRecentOfferingLabel ? <span>{mostRecentOfferingLabel}</span> : null}
            {course.distribs.length > 0 ? <span>{course.distribs.join(", ")}</span> : null}
            <span>{reviewCount} student reviews</span>
            {typicalMedianLabel ? (
              <span
                className="course-meta-button"
              >
                Typical Median: {typicalMedianLabel}
                {typicalMedianReviewCount > 0 ? ` · ${typicalMedianReviewCount} reported median${typicalMedianReviewCount === 1 ? "" : "s"}` : ""}
              </span>
            ) : null}
          </div>
        </div>
      </section>

      {recentOfferings.length > 0 ? (
        <section className="panel stack-md">
          <div className="section-heading">
            <p className="eyebrow">Schedule</p>
            <h2>Recent Dartmouth offerings</h2>
          </div>
          <div className="offerings-grid">
            {recentOfferings.map((offering) => (
              <article key={`${offering.term}-${offering.periods.join("/")}`} className="list-card offering-card">
                <strong>{offering.term}</strong>
                <span>{formatOffering(offering)}</span>
                <small>{offering.sectionCount} section{offering.sectionCount === 1 ? "" : "s"}</small>
                {offering.instructors.length > 0 ? (
                  <small>Instructors: {offering.instructors.join(", ")}</small>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {!loginWallActive ? (
        <section className="panel stack-md">
          <div className="section-heading">
            <p className="eyebrow">Contribute</p>
            <h2>Write a new review</h2>
          </div>
          {reviewMessage ? <p className="status-message" role="status" aria-live="polite">{reviewMessage}</p> : null}
          {pendingReviewMessage ? <p className="notice-banner">{pendingReviewMessage}</p> : null}
          {!props.session ? (
            <p>Log in with a Dartmouth email to add a review.</p>
          ) : props.session.isVerified && !pendingReviewState && canSubmitReview ? (
            <form className="review-form" onSubmit={handleSubmitReview}>
              <label>
                Professor
                <select
                  required
                  value={reviewDraft.professor}
                  onChange={(event) => setReviewDraft((current) => ({ ...current, professor: event.target.value }))}
                >
                  <option value="">Select a professor</option>
                  {allowedReviewProfessors.map((professor) => (
                    <option key={professor} value={professor}>
                      {professor}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Quarter
                <select
                  required
                  value={reviewDraft.quarter}
                  onChange={(event) => setReviewDraft((current) => ({ ...current, quarter: event.target.value }))}
                >
                  <option value="">Select a Dartmouth quarter</option>
                  {allowedReviewTerms.map((term) => (
                    <option key={term} value={term}>
                      {term}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Median
                <select
                  value={reviewDraft.median}
                  onChange={(event) => setReviewDraft((current) => ({
                    ...current,
                    median: event.target.value as typeof REVIEW_MEDIAN_OPTIONS[number],
                  }))}
                >
                  {REVIEW_MEDIAN_OPTIONS.map((median) => (
                    <option key={median} value={median}>
                      {median}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Review
                <textarea
                  required
                  minLength={50}
                  value={reviewDraft.comments}
                  onChange={(event) => setReviewDraft((current) => ({ ...current, comments: event.target.value }))}
                  placeholder="What would you tell another student before they enroll?"
                  rows={7}
                  aria-describedby="review-character-help"
                />
                <span className="review-character-help" id="review-character-help">
                  <span>Minimum 50 characters. Longer reviews are welcome.</span>
                  <span
                    className={reviewCharacterCount >= 50 ? "review-character-count is-valid" : "review-character-count"}
                    aria-live="polite"
                  >
                    {reviewCharacterCount} characters, minimum 50
                  </span>
                </span>
              </label>
              <TurnstileWidget onToken={setTurnstileToken} />
              <button type="submit" className="primary-link">
                Submit review
              </button>
            </form>
          ) : pendingReviewState ? (
            <p>You already have a review waiting to be published for this course.</p>
          ) : props.session.isVerified ? (
            <p>This course has not been offered for a while, so new reviews are temporarily disabled.</p>
          ) : (
            <p>Check your Dartmouth inbox and verify your email first.</p>
          )}
        </section>
      ) : null}

      <section className="panel stack-lg">
        <div className="section-heading">
          <p className="eyebrow">Reviews</p>
          <h2>Student reviews</h2>
        </div>
        {course.legacyReviewAccess === "restricted" ? (
          <p className="notice-banner">
            We know this course has older student reviews, but some past review text still could not be imported
            because the source site now requires an active session.
          </p>
        ) : null}
        <div className="review-list">
          <div className="review-toolbar">
            <label className="compact-field">
              <span>Professor</span>
              <select value={reviewProfessorFilter} onChange={(event) => setReviewProfessorFilter(event.target.value)}>
                <option value="">All professors</option>
                {reviewProfessorOptions.map((professor) => (
                  <option key={professor} value={professor}>
                    {professor}
                  </option>
                ))}
              </select>
            </label>
            <p className="toolbar-summary">
              {visibleReviews.length} review{visibleReviews.length === 1 ? "" : "s"}
              {reviewProfessorFilter ? ` for ${reviewProfessorFilter}` : ""}
            </p>
          </div>
          {showPendingReviewPreview && pendingReviewPreview ? (
            <article className="review-card pending-review-card">
              <div className="review-meta">
                <strong>{pendingReviewPreview.quarter}</strong>
                {renderReviewProfessor(pendingReviewPreview.professor)}
                {pendingReviewPreview.median ? <span>Median {pendingReviewPreview.median}</span> : null}
                <em>Pending nightly publish</em>
              </div>
              <p>{pendingReviewPreview.comments}</p>
            </article>
          ) : null}
          {visibleReviewPreview.map((review) => (
            <article key={review.id} className="review-card">
              <div className="review-meta">
                <strong>{review.term}</strong>
                {renderReviewProfessor(review.professor)}
                {review.median ? <span>Median {review.median}</span> : null}
              </div>
              <p>{review.comments}</p>
            </article>
          ))}
          {reviewPreviewWallActive && hiddenReviewPreview.length > 0 ? (
            <article className="review-preview-wall">
              <div className="blurred-review-stack" aria-hidden="true">
                {hiddenReviewPreview.slice(0, 2).map((review) => (
                  <article key={review.id} className="review-card">
                    <div className="review-meta">
                      <strong>{review.term}</strong>
                      <span>with {review.professor}</span>
                      {review.median ? <span>Median {review.median}</span> : null}
                    </div>
                    <p>{review.comments}</p>
                  </article>
                ))}
              </div>
              <div className="review-preview-overlay">
                <LoginWallCard
                  compact
                  title="Sign up or log in to view more course reviews"
                  body="Create an account or log in with your Dartmouth email to unlock all the reviews for this course."
                />
              </div>
            </article>
          ) : null}
          {visibleReviews.length === 0 ? (
            <p className="notice-banner">No reviews match that professor yet.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
